import React from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import CloseRounded from '@mui/icons-material/CloseRounded';

export interface ContextChip {
  key: string;
  label: string;
}

export interface ChatContextChipsProps {
  /** Where the answer comes from, e.g. "This board". Always shown, not removable. */
  base?: string;
  /** What narrows the answer, e.g. the page's filters. */
  chips: ContextChip[];
  /** Remove one, widening the next answer. Leave out to make chips fixed. */
  onRemove?: (key: string) => void;
}

/**
 * What the next answer can see, shown inside the composer so its scope is
 * never a guess. Removing a chip widens the question.
 */
export function ChatContextChips({ base, chips, onRemove }: ChatContextChipsProps) {
  if (!base && chips.length === 0) return null;
  return (
    <Box
      role="list"
      aria-label="What the answer can see"
      sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
    >
      {base && (
        <Typography
          role="listitem"
          variant="caption"
          sx={(t) => ({
            px: 1,
            borderRadius: `${t.ds.radius.full}px`,
            border: `1px solid ${t.tokens.brand.primarySoft}`,
            bgcolor: t.tokens.brand.primarySofter,
            color: t.tokens.text.accent,
            fontWeight: 500,
            lineHeight: '22px',
          })}
        >
          {base}
        </Typography>
      )}
      {chips.map((chip) => (
        <Box
          key={chip.key}
          role="listitem"
          sx={(t) => ({
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            pl: 1,
            pr: onRemove ? 0.25 : 1,
            borderRadius: `${t.ds.radius.full}px`,
            border: `1px solid ${t.tokens.border.subtle}`,
            bgcolor: t.tokens.surface.sunken,
          })}
        >
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.secondary, lineHeight: '22px' })}>
            {chip.label}
          </Typography>
          {onRemove && (
            <IconButton
              size="small"
              aria-label={`Remove ${chip.label}`}
              onClick={() => onRemove(chip.key)}
              sx={(t) => ({ p: 0.25, color: t.tokens.text.tertiary, '&:hover': { color: t.tokens.text.primary } })}
            >
              <CloseRounded sx={{ fontSize: 14 }} />
            </IconButton>
          )}
        </Box>
      ))}
    </Box>
  );
}
